/**
 * Terminal WebSocket Client
 * =========================
 *
 * WebSocket client for real-time terminal communication.
 * Handles bidirectional data flow between browser and server PTY.
 */

import { WS_BASE_URL } from './api-client';

/**
 * Terminal WebSocket message types
 */
export type TerminalMessageType = 'input' | 'output' | 'resize' | 'resized' | 'ping' | 'pong' | 'error';

export interface TerminalMessage {
  type: TerminalMessageType;
  data?: string;  // Base64 encoded for output
  cols?: number;
  rows?: number;
  message?: string;
}

/**
 * Terminal WebSocket event callbacks
 */
export interface TerminalWebSocketCallbacks {
  onOutput?: (data: Uint8Array) => void;
  onResize?: (cols: number, rows: number) => void;
  onError?: (error: string) => void;
  onClose?: (code: number, reason: string) => void;
  onOpen?: () => void;
}

/**
 * Terminal WebSocket connection state
 */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * WebSocket client for a single terminal session
 */
export class TerminalWebSocket {
  private ws: WebSocket | null = null;
  private sessionId: string;
  private callbacks: TerminalWebSocketCallbacks;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private pingInterval: number | null = null;
  private state: ConnectionState = 'disconnected';

  constructor(sessionId: string, callbacks: TerminalWebSocketCallbacks = {}) {
    this.sessionId = sessionId;
    this.callbacks = callbacks;
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Connect to the terminal WebSocket
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      this.state = 'connecting';
      const url = `${WS_BASE_URL}/ws/terminals/${this.sessionId}`;

      try {
        this.ws = new WebSocket(url);
        this.ws.binaryType = 'arraybuffer';

        this.ws.onopen = () => {
          this.state = 'connected';
          this.reconnectAttempts = 0;
          this.startPingInterval();
          this.callbacks.onOpen?.();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (event) => {
          console.error('Terminal WebSocket error:', event);
          this.state = 'error';
          this.callbacks.onError?.('WebSocket error');
        };

        this.ws.onclose = (event) => {
          this.state = 'disconnected';
          this.stopPingInterval();
          this.callbacks.onClose?.(event.code, event.reason);

          // Attempt reconnect if not intentionally closed
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        // Timeout for connection
        setTimeout(() => {
          if (this.state === 'connecting') {
            this.ws?.close();
            reject(new Error('Connection timeout'));
          }
        }, 10000);

      } catch (error) {
        this.state = 'error';
        reject(error);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: string | ArrayBuffer): void {
    try {
      let message: TerminalMessage;

      if (typeof data === 'string') {
        message = JSON.parse(data);
      } else {
        // Binary data - treat as raw terminal output
        const bytes = new Uint8Array(data);
        this.callbacks.onOutput?.(bytes);
        return;
      }

      switch (message.type) {
        case 'output':
          if (message.data) {
            // Decode base64 to Uint8Array
            const binary = atob(message.data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            this.callbacks.onOutput?.(bytes);
          }
          break;

        case 'resized':
          if (message.cols && message.rows) {
            this.callbacks.onResize?.(message.cols, message.rows);
          }
          break;

        case 'error':
          this.callbacks.onError?.(message.message || 'Unknown error');
          break;

        case 'pong':
          // Heartbeat response received
          break;
      }
    } catch (error) {
      console.error('Error handling terminal message:', error);
    }
  }

  /**
   * Send input to the terminal
   */
  sendInput(data: string): void {
    this.send({ type: 'input', data });
  }

  /**
   * Request terminal resize
   */
  resize(cols: number, rows: number): void {
    this.send({ type: 'resize', cols, rows });
  }

  /**
   * Send a ping to keep connection alive
   */
  private ping(): void {
    this.send({ type: 'ping' });
  }

  /**
   * Send a message through WebSocket
   */
  private send(message: TerminalMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = window.setInterval(() => this.ping(), 30000);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    setTimeout(() => {
      if (this.state !== 'connected') {
        this.connect().catch(console.error);
      }
    }, delay);
  }

  /**
   * Disconnect from the terminal
   */
  disconnect(): void {
    this.stopPingInterval();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.state = 'disconnected';
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

/**
 * Terminal WebSocket manager
 * Manages multiple terminal WebSocket connections
 */
export class TerminalWebSocketManager {
  private connections: Map<string, TerminalWebSocket> = new Map();

  /**
   * Create a new terminal WebSocket connection
   */
  async create(
    sessionId: string,
    callbacks: TerminalWebSocketCallbacks
  ): Promise<TerminalWebSocket> {
    // Close existing connection if any
    this.close(sessionId);

    const ws = new TerminalWebSocket(sessionId, callbacks);
    await ws.connect();

    this.connections.set(sessionId, ws);
    return ws;
  }

  /**
   * Get an existing connection
   */
  get(sessionId: string): TerminalWebSocket | undefined {
    return this.connections.get(sessionId);
  }

  /**
   * Close a terminal connection
   */
  close(sessionId: string): void {
    const ws = this.connections.get(sessionId);
    if (ws) {
      ws.disconnect();
      this.connections.delete(sessionId);
    }
  }

  /**
   * Close all connections
   */
  closeAll(): void {
    for (const [sessionId, ws] of this.connections) {
      ws.disconnect();
    }
    this.connections.clear();
  }

  /**
   * Get all active session IDs
   */
  getActiveSessions(): string[] {
    return Array.from(this.connections.keys()).filter(
      (id) => this.connections.get(id)?.isConnected()
    );
  }
}

// Global instance
export const terminalWsManager = new TerminalWebSocketManager();
